/* eslint-disable max-lines-per-function */
import { EventBus, Events, store } from "jiffy-store";
import { api } from "jiffy-api";
import { isCustomizableMadePage } from "components/jiffy/product-details/customizable-made-page-utils";
import MadeStore from "components/jiffy/vue-product-details-print/store/store";

const getWrapper = () => document.getElementById("js-product-mobile-grid");

const setLoadingStatus = loading => {
  const $loading = getWrapper();
  $loading.classList.toggle("product-details-variants-form__grid-wrapper--loading", loading);
};

const gridCache = new Map();
const inProgressRequests = new Map();

const getCacheKey = ({ productId, activeColor, pageMode, madeMode }) =>
  `${productId}-${activeColor}-${pageMode}-${madeMode}`;

const updateGridDisplay = ({ gridHtml, activeColor, pageMode, madeMode, callback }) => {
  const $wrapper = getWrapper();
  const dataHtml = document.createElement("div");
  dataHtml.innerHTML = gridHtml || "";
  $wrapper.innerHTML = dataHtml.innerHTML;
  if (callback instanceof Function) callback(activeColor, pageMode === "made" && madeMode);
};

const fetchGrid = async (
  productId,
  { activeColor, pageMode = "blanks", madeMode = "print_default" },
  callback
) => {
  setLoadingStatus(true);

  const cacheKey = getCacheKey({ productId, activeColor, pageMode, madeMode });

  if (inProgressRequests.has(cacheKey)) {
    return;
  }

  if (gridCache.has(cacheKey)) {
    const data = gridCache.get(cacheKey);
    updateGridDisplay({ gridHtml: data.grid, activeColor, pageMode, madeMode, callback });
    setLoadingStatus(false);
    return;
  }

  const payload = {
    ac: activeColor,
    made: pageMode === "made",
    print: madeMode
  };

  if (isCustomizableMadePage()) {
    payload.made_dedicated_pdp = true;
  }

  try {
    const request = api.product.fetchWithColorMobileGrid({ productId }, payload, {
      cacheable: true
    });

    inProgressRequests.set(cacheKey, request);

    const mobileGridResponse = await request;

    inProgressRequests.delete(cacheKey);
    gridCache.set(cacheKey, mobileGridResponse);

    const currentMode = MadeStore.getters["app/mode"];
    const currentMadeMode = MadeStore.getters["app/madeMode"];

    const currentCacheKey = getCacheKey({
      productId,
      activeColor,
      pageMode: currentMode,
      madeMode: currentMadeMode
    });

    if (currentCacheKey === cacheKey) {
      updateGridDisplay({
        gridHtml: mobileGridResponse.grid,
        activeColor,
        pageMode,
        madeMode,
        callback
      });
    }
  } catch (error) {
    inProgressRequests.delete(cacheKey);
    if (error && error.readyState !== 0) {
      window.Honeybadger.notify(`[JS] Error fetching quantity grid ${JSON.stringify(error)}`, {
        params: { productId, activeColor, pageMode, madeMode }
      });
    }
  } finally {
    setLoadingStatus(false);
  }
};

export default (productId, activeColor, callback) => {
  MadeStore.watch(
    state => ({
      newMode: state.app.mode,
      newMadeMode: state.app.madeMode
    }),
    ({ newMode, newMadeMode }) => {
      const color = MadeStore.getters["app/activeColor"];
      fetchGrid(
        productId,
        { activeColor: color, pageMode: newMode, madeMode: newMadeMode },
        callback
      );
    }
  );

  EventBus.on(Events.PRODUCT_COLOR_SELECTED, (_, { color }) => {
    const { loaded } = store.get(state => ({
      loaded: state.ui.inventoryLoaded,
      loading: state.ui.inventoryLoading
    }));

    const newMode = MadeStore.getters["app/mode"];
    const newMadeMode = MadeStore.getters["app/madeMode"];
    const payload = { activeColor: color, pageMode: newMode, madeMode: newMadeMode };

    if (loaded) {
      fetchGrid(productId, payload, callback);
    } else {
      setLoadingStatus(true);
      store.pdp.change({ isFetchingInventory: true, fetchingSlug: productId });

      const listener = store.ui.changed((__, { data }) => {
        if (data.inventoryLoaded) {
          fetchGrid(productId, payload, callback);
          listener && listener();
        }
      });
    }
  });
};
